// ─── Tab de alteração administrativa de senha com 2º Nível de Autorização ───
import { useState } from 'react';
import { Eye, EyeOff, RefreshCw, Save, KeyRound, Lock, Unlock, ShieldCheck, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../api/client';
import { ROUTES } from '../../../api/routes';
import { InviteStatusCard } from '../../../components/invite';
import { PasswordConfirmModal } from '../../../components/ui';
import type { Usuario } from './usuarios.types';

/** Gera senha aleatória legível */
function gerarSenhaAleatoria(len = 10): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#';
  let s = '';
  for (let i = 0; i < len; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

interface Props {
  user: Usuario;
  onUpdated?: () => void;
  onResend?: (userId: string) => void;
  resending?: boolean;
  newActivationLink?: string | null;
}

export function UsuariosSenhaTab({
  user,
  onUpdated,
  onResend,
  resending,
  newActivationLink,
}: Props) {
  const [novaSenha, setNovaSenha] = useState('');
  const [showSenha, setShowSenha] = useState(false);
  const [saving, setSaving] = useState(false);

  // 2º Nível de Autorização
  const [unlocked, setUnlocked] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleGerar = () => {
    const s = gerarSenhaAleatoria();
    setNovaSenha(s);
    setShowSenha(true);
  };

  const handleSalvar = async () => {
    if (!novaSenha || novaSenha.length < 8) {
      toast.error('A senha deve ter pelo menos 8 caracteres');
      return;
    }
    setSaving(true);
    try {
      await api.patch(`${ROUTES.admin.usuarios}/${user.id}/admin-reset-password`, {
        novaSenha,
      });
      toast.success('Senha administrativa alterada com sucesso!');
      setNovaSenha('');
      setShowSenha(false);
      setUnlocked(false);
      onUpdated?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao alterar senha');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* 1. Card Principal de Status e Controle de Acesso (Topo) */}
      {user && (
        <InviteStatusCard
          user={user as any}
          onResend={onResend}
          onStatusChanged={() => onUpdated?.()}
          resending={resending}
          newActivationLink={newActivationLink}
        />
      )}

      {/* 2. Card de Definição Manual de Senha (Protegida por 2º Nível) */}
      <div style={{
        background: '#ffffff',
        border: '1px solid var(--border-color, #e2e8f0)',
        borderRadius: 12,
        padding: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <KeyRound size={18} style={{ color: '#6366f1' }} />
            <div>
              <div style={{ fontSize: '.84rem', fontWeight: 700, color: 'var(--text-main, #1e293b)' }}>
                Definição Manual de Senha
              </div>
              <div style={{ fontSize: '.72rem', color: 'var(--text-muted, #64748b)' }}>
                Atribuição manual de senha administrativa direta.
              </div>
            </div>
          </div>

          {unlocked && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: '.68rem',
              fontWeight: 700,
              color: '#047857',
              background: '#10b98115',
              padding: '2px 8px',
              borderRadius: 6,
              border: '1px solid #10b98130'
            }}>
              <ShieldCheck size={12} /> Autorizado (2º Nível)
            </span>
          )}
        </div>

        {!unlocked ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'var(--bg-muted, #f8fafc)',
            padding: '12px 14px',
            borderRadius: 8,
            border: '1px solid var(--border-color, #e2e8f0)',
            gap: 12,
            flexWrap: 'wrap'
          }}>
            <span style={{ fontSize: '.75rem', color: '#64748b' }}>
              Por conformidade de segurança, a inserção manual de senha requer autorização de 2º nível.
            </span>
            <button
              type="button"
              className="btn-primary"
              onClick={() => setShowAuthModal(true)}
              style={{
                padding: '7px 14px',
                fontSize: '.78rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 700,
                whiteSpace: 'nowrap'
              }}
            >
              <Lock size={14} /> Desbloquear Senha Manual
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 4 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  className="form-input"
                  type={showSenha ? 'text' : 'password'}
                  value={novaSenha}
                  onChange={e => setNovaSenha(e.target.value)}
                  placeholder="Digite a nova senha (mínimo de 8 caracteres)"
                  style={{ background: '#ffffff', color: '#1e293b' }}
                />
                <button
                  type="button"
                  onClick={() => setShowSenha(!showSenha)}
                  style={{
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6b7280',
                  }}
                >
                  {showSenha ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              <button
                type="button"
                className="btn-cancel"
                onClick={handleGerar}
                style={{ whiteSpace: 'nowrap', fontSize: '.8rem', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <RefreshCw size={14} /> Gerar
              </button>

              <button
                type="button"
                className="btn-save"
                onClick={handleSalvar}
                disabled={saving || !novaSenha || novaSenha.length < 8}
                style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
              >
                <Save size={14} />
                {saving ? 'Salvando...' : 'Salvar Senha'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal Padronizado de Autorização 2º Nível */}
      <PasswordConfirmModal
        open={showAuthModal}
        title="Autorização de Segurança (2º Nível)"
        description="Digite a palavra-chave de autorização administrativa para habilitar a definição manual de senha:"
        requiredPassword="marcelo"
        confirmLabel="Liberar Acesso"
        onCancel={() => setShowAuthModal(false)}
        onConfirm={() => {
          setUnlocked(true);
          setShowAuthModal(false);
        }}
      />
    </div>
  );
}
